import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    TablePagination,
    TextField,
    InputAdornment,
    Breadcrumbs,
    Link,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { ArrowBack, Search, Refresh, Inventory, Store } from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { getVendorInventory } from '../../services/Api';

const ViewInventory = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [vendorInfo, setVendorInfo] = useState(null);

    // Pagination and Search state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [stockStatusFilter, setStockStatusFilter] = useState('');

    const { id } = useParams();
    const navigate = useNavigate();
   
    const vendorId = id; // Map route parameter to vendorId
    // Debounce effect: update debouncedSearch after 1s of no typing
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0); // Reset to first page on search
        }, 1000);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchInventory = async () => {
        if (!vendorId) {
            setError('Vendor ID is missing. Please navigate from the vendors list.');
            return;
        }
        
        setLoading(true);
        setError(null);
        try {
            
            const response = await getVendorInventory(vendorId, page + 1, rowsPerPage, debouncedSearch);

            if (response.success || response.data) {
                const src = response.data || response;
                let targetArray = [];

                if (Array.isArray(src)) {
                    targetArray = src;
                } else if (src && typeof src === 'object') {
                    if (Array.isArray(src.products)) targetArray = src.products;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                    else if (Array.isArray(src.docs)) targetArray = src.docs;
                    else if (Array.isArray(src.items)) targetArray = src.items;
                }

                const total = response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || 0;

               
                setProducts(targetArray);
                setTotalRows(total);

                // Set vendor info if available
                if (src.vendor) {
                    setVendorInfo(src.vendor);
                }
            } else {
                setError(response.message || 'Failed to fetch vendor inventory');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching inventory');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (vendorId) {
            fetchInventory();
        } else {
            setError('Vendor ID is missing. Please navigate from the vendors list.');
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId, page, rowsPerPage, debouncedSearch, stockStatusFilter]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(amount || 0);
    };

    const getStockStatus = (stockQuantity, lowStockAlert = 10) => {
        if (stockQuantity === 0) {
            return { label: 'Out of Stock', color: 'error.main', value: 'out-of-stock' };
        } else if (stockQuantity <= lowStockAlert) {
            return { label: 'Low Stock', color: 'warning.main', value: 'low-stock' };
        } else {
            return { label: 'In Stock', color: 'success.main', value: 'in-stock' };
        }
    };

    const getFilteredProducts = () => {
        if (!stockStatusFilter) {
            return products;
        }
        
        return products.filter(product => {
            const stockStatus = getStockStatus(product.stockQuantity, product.lowStockAlert);
            return stockStatus.value === stockStatusFilter;
        });
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton 
                        onClick={() => navigate('/vendors')} 
                        sx={{ mr: 2, color: 'primary.main' }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link component={RouterLink} to="/vendors" color="inherit">
                            Vendors
                        </Link>
                        <Typography color="text.primary">Vendor Inventory</Typography>
                    </Breadcrumbs>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                            <Inventory sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                            Vendor Inventory
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            View and manage vendor product inventory
                        </Typography>
                        {vendorInfo && (
                            <Typography variant="body1" color="text.primary" sx={{ mt: 1 }}>
                                <Store sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                                {vendorInfo.shopName || vendorInfo.name}
                            </Typography>
                        )}
                        {!vendorInfo && (
                            <Typography variant="body1" color="text.primary" sx={{ mt: 1 }}>
                                <Store sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
                                Vendor ID: {vendorId}
                            </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search products..."
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                width: 250,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '20px',
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel id="stock-status-filter-label">Stock Status</InputLabel>
                            <Select
                                labelId="stock-status-filter-label"
                                value={stockStatusFilter}
                                label="Stock Status"
                                onChange={(e) => setStockStatusFilter(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '20px',
                                        backgroundColor: 'rgba(0,0,0,0.02)',
                                    }
                                }}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="in-stock">In Stock</MenuItem>
                                <MenuItem value="low-stock">Low Stock</MenuItem>
                                <MenuItem value="out-of-stock">Out of Stock</MenuItem>
                            </Select>
                        </FormControl>
                        <IconButton onClick={fetchInventory} disabled={loading} sx={{ color: 'primary.main' }}>
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="inventory table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Variant</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Discount</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Stock</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Stock Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                const filteredProducts = getFilteredProducts();
                                return (
                                    <>
                                        {loading && products.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                                    <CircularProgress />
                                                </TableCell>
                                            </TableRow>
                                        ) : filteredProducts.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                                    <Typography color="text.secondary">
                                                        {stockStatusFilter 
                                                            ? `No products with ${stockStatusFilter.replace('-', ' ')} status found` 
                                                            : (debouncedSearch 
                                                                ? `No products matching "${debouncedSearch}"` 
                                                                : "No products found in inventory")}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredProducts.map((product) => {
                                                const stockStatus = getStockStatus(product.stockQuantity, product.lowStockAlert);
                                                return (
                                                <TableRow key={product.id || product._id} hover>
                                                    <TableCell>
                                                        <Avatar 
                                                            src={product.image} 
                                                            alt={product.name}
                                                            variant="rounded"
                                                            sx={{ width: 56, height: 56 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {product.name}
                                                        </Typography>
                                                        {product.information && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {product.information}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {product.productVariant || 'N/A'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="600">
                                                                {formatCurrency(product.discountPrice || product.price)}
                                                            </Typography>
                                                            {product.discountPrice && product.discountPrice !== product.price && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                                    {formatCurrency(product.price)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {product.discountPercentage ? (
                                                            <Chip
                                                                label={`${product.discountPercentage}% OFF`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: 'success.light',
                                                                    color: 'success.contrastText',
                                                                    fontWeight: 600
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary">
                                                                No discount
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography 
                                                            variant="body1" 
                                                            sx={{ 
                                                                fontWeight: 600,
                                                                color: product.stockQuantity <= (product.lowStockAlert || 10) ? 'error.main' : 'text.primary'
                                                            }}
                                                        >
                                                            {product.stockQuantity || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={stockStatus.label}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: stockStatus.color,
                                                                color: 'white',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={product.isActive ? "Active" : "Inactive"}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: product.isActive ? 'success.main' : 'grey.400',
                                                                color: 'white',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                        )}
                                    </>
                                );
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default ViewInventory;